import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareCustomer } from './customers.prepare';

export type BaseOrganisation = API_JSON & {
  name: string;
  identifier: string;
  phone?: string | null;
  street?: string | null;
  suburb?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  rating?: number | null;
  customerType: API_JSON;
};

const order = { 'order[identifier]': 'desc' } as const;
const groups = ['customer-details'];
/**
 * Fetch organisations
 * @param params can be used to customise/limit the organisations retrieved
 * @returns an array of organisations
 */
export const getOrganisations: GetMany<BaseOrganisation> = async (params) =>
  (await ASSIST_API.get('customers', { params: requestParams({ order, groups }, params) })).data;

/**
 * Fetch organisation (singular)
 * @param params can be used to customise/limit the organisation retrieved
 * @returns a single organisation
 */
export const getOrganisation: GetOne<BaseOrganisation> = async (id, params) =>
  (await ASSIST_API.get(`customers/${id}`, { params: requestParams({ order, groups }, params) }))
    .data;

/**
 * Hook implementation to fetch organisations
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns customers matching request
 */
export const useOrganisations: AssistQuery.UseMany<BaseOrganisation> = (params, options) =>
  useQueryAssist(['customers', params], () => getOrganisations(params), options);

/**
 * Given a customerId fetch and return the organisation
 *  - only runs once customerId is provided
 *  - fetches the one organisation
 *  - caches data
 * @param id customerId
 * @param params optional parameters to change requested data
 * @returns the matching organisation
 */
export const useOrganisation: AssistQuery.UseOne<BaseOrganisation> = (id, params, options) =>
  useQueryAssist(['customers', { id, ...params }], () => getOrganisation(id, params), {
    ...options,
    enabled: !!id
  });

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePatchOrganisation: AssistQuery.UseMutation<BaseOrganisation, BaseOrganisation> = (
  options
) =>
  useMutation(
    (customer: BaseOrganisation) =>
      ASSIST_API.patch(`customers/${customer.id}`, prepareCustomer(customer), {
        params: requestParams({ groups })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a user ID and area ID
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostOrganisation: AssistQuery.UseMutation<BaseOrganisation, BaseOrganisation> = (
  options
) =>
  useMutation(
    (customer: BaseOrganisation) =>
      ASSIST_API.post('customers/create', prepareCustomer(customer), {
        params: requestParams({ groups })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['customers']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );
